import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { UserPlus } from 'lucide-react';
import { useState, type JSX } from 'react';

import { usePlatformPermissions } from '@/features/auth/hooks/usePlatformPermissions';
import { PLATFORM_SESSION_QUERY_KEY, platformRoleLabel } from '@/lib/permissions';

import styles from './AdminsPage.module.scss';
import {
  listPlatformAdmins,
  PLATFORM_ADMINS_QUERY_KEY,
  renewPlatformAdminInvitation,
  type PlatformAdmin,
  type PlatformAdminInvitation,
  type PlatformAdminStatus,
} from '../api/platform-admins.graphql';
import { AdminActionDialog, type AdminAction } from '../components/AdminActionDialog';
import { ChangeRoleDialog } from '../components/ChangeRoleDialog';
import { InvitationLinkDialog } from '../components/InvitationLinkDialog';
import { InviteAdminDialog } from '../components/InviteAdminDialog';

const STATUS_LABELS: Record<PlatformAdminStatus, string> = {
  ACTIVE: 'Activo',
  INACTIVE: 'Inactivo',
  INVITED: 'Invitación pendiente',
  INVITATION_EXPIRED: 'Invitación vencida',
};

const DONE_MESSAGES: Record<AdminAction, string> = {
  deactivate: 'desactivado: perdió el acceso de inmediato',
  reactivate: 'reactivado',
  'reset-mfa': 'se cerraron sus sesiones y deberá configurar el MFA en su próximo inicio de sesión',
  unlock: 'desbloqueado',
};

/** OS-1717 — aviso según el resultado real del email (nunca "enviada" si no se confirmó). */
function invitationNotice(invitation: PlatformAdminInvitation): string {
  const email = invitation.admin.email;
  return invitation.emailDelivery.status === 'SENT'
    ? `Invitación enviada por email a ${email}.`
    : `Invitación creada para ${email}; el email no se confirmó: comparte el link.`;
}

function formatDateTime(value: string | null): string {
  return value
    ? new Date(value).toLocaleString('es-CO', { dateStyle: 'medium', timeStyle: 'short' })
    : '—';
}

type Dialog =
  | { kind: 'invite' }
  | { kind: 'link'; invitation: PlatformAdminInvitation }
  | { kind: 'role'; admin: PlatformAdmin }
  | { kind: 'action'; action: AdminAction; admin: PlatformAdmin }
  | null;

/**
 * OS-1717 — sección Administradores (solo SUPER): invitar, asignar rol, desactivar, reiniciar MFA,
 * desbloquear y ver el último acceso. Las reglas (último SUPER, acciones sobre uno mismo) las
 * aplica el API; aquí solo se ocultan las acciones que no aplican.
 */
export function AdminsPage(): JSX.Element {
  const qc = useQueryClient();
  const { session } = usePlatformPermissions();
  const [dialog, setDialog] = useState<Dialog>(null);
  const [notice, setNotice] = useState('');
  const [renewError, setRenewError] = useState('');

  const query = useInfiniteQuery({
    queryKey: PLATFORM_ADMINS_QUERY_KEY,
    queryFn: ({ pageParam }) => listPlatformAdmins(pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => (last.hasNextPage ? last.nextCursor : undefined),
  });
  const admins = query.data?.pages.flatMap((page) => page.edges) ?? [];

  function changed(message: string): void {
    setDialog(null);
    setNotice(message);
    void qc.invalidateQueries({ queryKey: PLATFORM_ADMINS_QUERY_KEY });
    // Un cambio sobre uno mismo (p. ej. dejar de ser Super) ajusta menús y acceso al momento.
    void qc.invalidateQueries({ queryKey: PLATFORM_SESSION_QUERY_KEY });
  }

  async function renew(admin: PlatformAdmin): Promise<void> {
    setRenewError('');
    setNotice('');
    try {
      const invitation = await renewPlatformAdminInvitation(admin.id);
      void qc.invalidateQueries({ queryKey: PLATFORM_ADMINS_QUERY_KEY });
      setDialog({ kind: 'link', invitation });
    } catch (error) {
      setRenewError((error as Error).message);
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}>Administradores de plataforma</h1>
          <p className={styles.muted}>
            Cada acción pide un motivo y queda en la auditoría. Los cambios de rol y de estado rigen
            en la siguiente acción de la persona, sin cerrar su sesión.
          </p>
        </div>
        <button
          type="button"
          className={styles.primaryBtn}
          onClick={() => {
            setNotice('');
            setDialog({ kind: 'invite' });
          }}
        >
          <UserPlus size={16} aria-hidden="true" /> Invitar administrador
        </button>
      </div>

      <p className={styles.notice} role="status">
        {notice}
      </p>
      {renewError ? (
        <p className={styles.error} role="alert">
          {renewError}
        </p>
      ) : null}

      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <caption className={styles.srOnly}>Administradores de plataforma</caption>
          <thead>
            <tr>
              <th scope="col">Email</th>
              <th scope="col">Rol</th>
              <th scope="col">Estado</th>
              <th scope="col">MFA</th>
              <th scope="col">Último acceso</th>
              <th scope="col">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {query.isLoading ? (
              <tr>
                <td colSpan={6} className={styles.empty}>
                  Cargando…
                </td>
              </tr>
            ) : null}
            {query.isError && !query.data ? (
              <tr>
                <td colSpan={6} className={styles.empty} role="alert">
                  No se pudieron cargar los administradores: {query.error.message}{' '}
                  <button type="button" className={styles.linkBtn} onClick={() => void query.refetch()}>
                    Reintentar
                  </button>
                </td>
              </tr>
            ) : null}
            {admins.map((admin) => {
              const isSelf = admin.id === session?.id;
              const invited = admin.status === 'INVITED' || admin.status === 'INVITATION_EXPIRED';
              return (
                <tr key={admin.id}>
                  <td data-label="Email">
                    <span>
                      {admin.email}
                      {isSelf ? <span className={styles.self}> (tú)</span> : null}
                    </span>
                  </td>
                  <td data-label="Rol">{platformRoleLabel(admin.role)}</td>
                  <td data-label="Estado">
                    <span className={styles.statusCell}>
                    <span className={admin.status === 'ACTIVE' ? styles.badgeOk : styles.badgeOff}>
                      {STATUS_LABELS[admin.status]}
                    </span>
                    {admin.lockedUntil ? (
                      <span className={styles.badgeWarn}>
                        Bloqueado hasta {formatDateTime(admin.lockedUntil)}
                      </span>
                    ) : null}
                    {admin.status === 'INVITED' ? (
                      <span className={styles.muted}> · vence {formatDateTime(admin.invitationExpiresAt)}</span>
                    ) : null}
                    </span>
                  </td>
                  <td data-label="MFA">{admin.mfaEnabled ? 'Configurado' : 'Pendiente'}</td>
                  <td data-label="Último acceso">{formatDateTime(admin.lastActivityAt)}</td>
                  <td data-label="Acciones">
                    <div className={styles.actions}>
                      <button
                        type="button"
                        className={styles.actionBtn}
                        aria-label={`Cambiar rol de ${admin.email}`}
                        onClick={() => setDialog({ kind: 'role', admin })}
                      >
                        Cambiar rol
                      </button>
                      {invited ? (
                        <button
                          type="button"
                          className={styles.actionBtn}
                          aria-label={`Renovar invitación de ${admin.email}`}
                          onClick={() => void renew(admin)}
                        >
                          Renovar invitación
                        </button>
                      ) : null}
                      {admin.lockedUntil ? (
                        <button
                          type="button"
                          className={styles.actionBtn}
                          aria-label={`Desbloquear ${admin.email}`}
                          onClick={() => setDialog({ kind: 'action', action: 'unlock', admin })}
                        >
                          Desbloquear
                        </button>
                      ) : null}
                      {admin.mfaEnabled && !isSelf ? (
                        <button
                          type="button"
                          className={styles.actionBtn}
                          aria-label={`Reiniciar MFA de ${admin.email}`}
                          onClick={() => setDialog({ kind: 'action', action: 'reset-mfa', admin })}
                        >
                          Reiniciar MFA
                        </button>
                      ) : null}
                      {admin.status === 'INACTIVE' ? (
                        <button
                          type="button"
                          className={styles.actionBtn}
                          aria-label={`Reactivar ${admin.email}`}
                          onClick={() => setDialog({ kind: 'action', action: 'reactivate', admin })}
                        >
                          Reactivar
                        </button>
                      ) : null}
                      {admin.status !== 'INACTIVE' && !isSelf ? (
                        <button
                          type="button"
                          className={`${styles.actionBtn} ${styles.danger}`}
                          aria-label={`Desactivar ${admin.email}`}
                          onClick={() => setDialog({ kind: 'action', action: 'deactivate', admin })}
                        >
                          Desactivar
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {query.hasNextPage ? (
          <div className={styles.loadMore}>
            <button
              type="button"
              className={styles.secondaryBtn}
              disabled={query.isFetchingNextPage}
              onClick={() => void query.fetchNextPage()}
            >
              {query.isFetchingNextPage ? 'Cargando…' : 'Cargar más'}
            </button>
          </div>
        ) : null}
      </div>

      {dialog?.kind === 'invite' ? (
        <InviteAdminDialog
          onInvited={(invitation) => {
            void qc.invalidateQueries({ queryKey: PLATFORM_ADMINS_QUERY_KEY });
            setNotice(invitationNotice(invitation));
            setDialog({ kind: 'link', invitation });
          }}
          onCancel={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === 'link' ? (
        <InvitationLinkDialog invitation={dialog.invitation} onClose={() => setDialog(null)} />
      ) : null}
      {dialog?.kind === 'role' ? (
        <ChangeRoleDialog
          admin={dialog.admin}
          isSelf={dialog.admin.id === session?.id}
          onDone={(admin) => changed(`${admin.email} ahora es ${platformRoleLabel(admin.role)}.`)}
          onCancel={() => setDialog(null)}
        />
      ) : null}
      {dialog?.kind === 'action' ? (
        <AdminActionDialog
          action={dialog.action}
          admin={dialog.admin}
          onDone={(admin) => changed(`${admin.email}: ${DONE_MESSAGES[dialog.action]}.`)}
          onCancel={() => setDialog(null)}
        />
      ) : null}
    </div>
  );
}
